"use client";
import cx from "classnames";
import { useEffect, useState, memo, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { format } from "date-fns";
import { isDocumentType } from "@/lib/dropDownData";
import {
  PencilEditIcon,
  SparklesIcon,
  BoxIcon,
  CrossIcon,
  CopyIcon,
  DownloadIcon,
  EyeIcon,
  ShareIcon,
} from "./icons";
import { Markdown } from "./markdown";
import equal from "fast-deep-equal";
import {
  cn,
  sanitizeText,
  handleDownload,
  handleView,
  downloadCardFromURL,
} from "@/lib/utils";
import { DocumentPreview } from "./document-preview";
import { useDataStream } from "./data-stream-provider";
import { useArtifact } from "@/hooks/use-artifact";
import BuyCreditsModal from "./BuyCreditsModal";
import { ShareLinkPopup } from "./share-link-popup";
import { useSelector, useDispatch } from "react-redux";
import { setShareData, setActiveItem } from "@/store/mainSlice";
import { Zap } from "lucide-react";
import { MessageEditor } from "./message-editor";

const PurePreviewMessage = ({
  chatId,
  message,
  isLoading,
  setMessages,
  regenerate,
  isReadonly,
  requiresScrollPadding,
  showCode,
  initialScreenId,
  userId,
  isLast
}) => {
  const [mode, setMode] = useState("view");
  const { artifact, setArtifact } = useArtifact();
  // const [userDetail, setUserDetail] = useState(null);
  // const [isFreeLimit, setIsFreeLimit] = useState(true);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [generateLinkData, setGenerateLinkData] = useState(null);
  const [htmlContent, setHtmlContent] = useState(null);
  const [creditUsedFromLog, setCreditUsedFromLog] = useState(null);
  const [messageTimestamp, setMessageTimestamp] = useState(null);

  const { shareData, liveWebsiteUrl, userData, isFreeLimit, activeTab } = useSelector((state) => state.main);
  const dispatch = useDispatch();
  const pathname = usePathname();
  const user_Id = userData._id;

  const chatType = !activeTab
    ? "Chat Preview"
    : activeTab === "Card Maker"
      ? "Card Preview"
      : activeTab === "Page Builder"
        ? "Page Preview"
        : `${activeTab} Preview`;

  useEffect(() => {
    const fetchChatHistory = async () => {
      if (!message.id) return;
      try {
        let apiUrl;
        let isProject = pathname.startsWith("/project");
        // 🔥 If the route contains `/project`, call project logs API
        if (isProject) {
          apiUrl = `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/project/logs/${message.id}`;
        } else {
          apiUrl = `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website/logs/${message.id}`;
        }
        const res = await fetch(apiUrl);
        if (!res.ok) throw new Error("Failed to fetch chat logs");
        const data = await res.json();
        if (!data.logs || data.logs.length === 0) return;

        const lastLog = data.logs[data.logs.length - 1];
        if (isProject) {
          if (isDocumentType(lastLog.chat_type) || lastLog.chat_type == "chat") {
            setHtmlContent(lastLog.content);
          } else {
            const htmlRes = await fetch(lastLog.content); // lastLog.content is URL in /project
            const htmlText = await htmlRes.text();
            setHtmlContent(htmlText);
          }
        } else {
          if (isDocumentType(lastLog.chat_type) || lastLog.chat_type == "chat") {
            // Use plain text content for Business Plan
            setHtmlContent(lastLog.content || "No content found");
          } else {
            const htmlRes = await fetch(lastLog.html_file_detail);
            const htmlText = await htmlRes.text();
            setHtmlContent(htmlText);
          }
        }

        const matchingLog = data.logs.find(
          (log) => log.prompt?.trim() === message.parts?.[0]?.text?.trim()
        );

        if (matchingLog?.creditUsed != null)
          setCreditUsedFromLog(matchingLog.creditUsed);
        if (matchingLog?.createdAt) setMessageTimestamp(matchingLog.createdAt);

        const shareDataObj = {
          // websiteUrl: lastLog.html_file_detail,
          websiteUrl: !isProject ? lastLog.html_file_detail : lastLog.content,
          screenId: lastLog.screen_id,
          websiteDetailsUid: lastLog._id,
          userId: user_Id,
        };

        setGenerateLinkData(shareDataObj);
        dispatch(setShareData(shareDataObj));
      } catch (err) {
        console.error("Error fetching chat history:", err);
      }
    };

    fetchChatHistory();
  }, [pathname, message.id]);

  const extractedCredit = useMemo(() => {
    if (creditUsedFromLog != null) return creditUsedFromLog;

    const creditPart = message.parts?.find((part) =>
      /\*\*[\s⚡]*Credit Used:\s*(\d+)/i.test(part.text || "")
    );
    if (creditPart?.text) {
      const match = creditPart.text.match(/\*\*[\s⚡]*Credit Used:\s*(\d+)/i);
      if (match && match[1]) return Number(match[1]);
    }
    return null;
  }, [message.parts, creditUsedFromLog]);

  useDataStream();

  // const handleShareClick = () => setShareOpen(true);
  const handleShareClick = () => {
    dispatch(setActiveItem("z-[40]")); // update Redux state
    setShareOpen(true);                 // open share modal
  };
  // const isFreeLimitBool = isFreeLimit === true || isFreeLimit === "true";

  const getMessageTimestamp = () => {
    if (messageTimestamp) return messageTimestamp;
    if (message.metadata?.createdAt) return message.metadata.createdAt;
    return null;
  };

  const displayTimestamp = getMessageTimestamp();
  return (
    <AnimatePresence>
      <motion.div
        data-testid={`message-${message.role}`}
        className="w-full group/message mr-auto"
        initial={{ y: 5, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        data-role={message.role}
      >
        <div
          className={cn(
            "flex message-xl gap-4 w-full group-data-[role=user]/message:ml-auto group-data-[role=user]/message:max-w-2xl",
            {
              "w-full": mode === "edit",
              "group-data-[role=user]/message:w-fit": mode !== "edit",
            }
          )}
        >
          <div
            className={cn("flex flex-col gap-4 w-full", {
              "min-h-96": message.role === "assistant" && requiresScrollPadding,
            })}
          >
            {message.parts?.map((part, index) => {
              const { type } = part;
              const key = `message-${message.id}-part-${index}`;
              if (type === "text") {
                if (mode === "view") {
                  const isCreditText = /\*\*[\s⚡]*Credit Used:\s*(\d+)/i.test(
                    part.text || ""
                  );
                  if (isCreditText && message.role === "user") return null;

                  return (
                    <div
                      key={key}
                      className={cn("flex flex-row gap-2 items-start", {
                        "justify-end": message.role === "user",
                      })}
                    >
                      {message.role === "assistant" && (
                        <div className="size-8 flex items-center rounded-full justify-center ring-1 shrink-0 ring-border bg-background">
                          <SparklesIcon size={14} />
                        </div>
                      )}
                      <div
                        data-testid="message-content"
                        className={cn("message-design flex flex-col", {
                          "bg-primary text-primary-foreground bg-[#e9e9e980] text-[#383a42] dark:bg-[#323232d9] dark:text-[#ffffff] p-4 rounded-xl text-[16px] leading-[1.5]":
                            message.role === "user",
                        })}
                      >
                        {/* 🖼️ Image Preview Design */}
                        {part.url && (
                          <div className="relative group mb-2">
                            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                              <img
                                src={
                                  part.url?.includes("storage")
                                    ? `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/${part.url}` // ✅ From storage folder
                                    : typeof part.url === "string"
                                      ? part.url // ✅ It's already a valid URL or base64
                                      : URL.createObjectURL(part.url) // ✅ For File/Blob objects
                                }
                                alt="User provided"
                                style={{
                                  // width: "100px",
                                  height: "100px"
                                }}
                                className="w-full max-h-[280px] object-cover rounded-xl transition-transform duration-300 group-hover:scale-105"
                              />
                            </div>
                          </div>
                        )}

                        {/* 💬 Message text */}
                        {part.type === "text" && <Markdown>
                          {sanitizeText(part.text)}
                        </Markdown>}

                        {/* ⚡ Credit Used badge */}
                        {part.type === "text" && message.role === "user" && extractedCredit != null && (
                          <div className="w-full flex justify-end">
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-800 shadow-sm dark:bg-sidebar dark:text-white">
                              <Zap size={16} />
                              <span>Credit Used: {extractedCredit}</span>
                            </span>
                          </div>
                        )}
                      </div>

                    </div>
                  );
                }

                if (mode === "edit") {
                  return (
                    <div
                      className="flex w-full flex-row items-start gap-3"
                      key={key}
                    >
                      <div className="size-8" />
                      <div className="min-w-0 flex-1">
                        <MessageEditor
                          key={message.id}
                          message={message}
                          regenerate={regenerate}
                          setMessages={setMessages}
                          setMode={setMode}
                        />
                      </div>
                    </div>
                  );
                }
              }

              if (type === "data-html") {
                if (artifact?.isVisible) return null;
                if (!part.data?.trim())
                  return (
                    <div key={key} className="text-sm text-muted-foreground italic">
                      No preview available.
                    </div>
                  );

                return (
                  <div key={key} className="my-2 custom-open-iframe">
                    <DocumentPreview
                      isReadonly={isReadonly}
                      result={{
                        url: part.data,
                        kind: "html",
                        title: chatType
                      }}
                      isLast={isLast}
                    />
                    <div className="flex items-center gap-1 mt-2 bottomBar">
                      {
                        isLast && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                if (isFreeLimit) return setShowBuyCreditsModal(true);
                                if (activeTab === "Card Maker") {
                                  const contentToUse =
                                    artifact.kind === "html"
                                      ? htmlContent || artifact.content
                                      : liveWebsiteUrl;
                                  downloadCardFromURL(contentToUse, message.idl)
                                } else {
                                  handleDownload(liveWebsiteUrl, message.id, "patt");
                                }
                              }}
                              className="inline-flex items-center justify-center rounded-md p-2 hover:bg-accent text-muted-foreground dark:text-[hsl(240,17.61%,81.67%)] hover:text-foreground transition"
                              title="Download HTML"
                            >
                              <DownloadIcon size={20} />
                            </button>

                            {activeTab !== "Card Maker" && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (isFreeLimit) return setShowBuyCreditsModal(true);
                                    handleView(liveWebsiteUrl, message.id, "patt");
                                  }}
                                  className="inline-flex items-center justify-center rounded-md p-2 hover:bg-accent text-muted-foreground dark:text-[hsl(240,17.61%,81.67%)] hover:text-foreground transition"
                                  title="Preview HTML"
                                >
                                  <EyeIcon size={20} />
                                </button>
                                <button
                                  type="button"
                                  onClick={handleShareClick}
                                  className="inline-flex items-center justify-center rounded-md p-2 hover:bg-accent text-muted-foreground dark:text-[hsl(240,17.61%,81.67%)] hover:text-foreground transition"
                                  title="Share Link"
                                >
                                  <ShareIcon size={20} />
                                </button>
                              </>
                            )}
                          </>
                        )
                      }

                      {displayTimestamp && (
                        <div
                          className={cn(
                            "text-xs text-muted-foreground mt-1 px-2 ml-auto",
                            message.role === "user" ? "text-right" : "text-left"
                          )}
                        >
                          {format(new Date(displayTimestamp), "MMM d, yyyy 'at' h:mm a")}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              if (type === "tool-createDocument") {
                const { toolCallId } = part;
                if (typeof part.text === "object" && part.text?.error) {
                  return (
                    <div
                      className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-500 dark:bg-red-950/50"
                      key={toolCallId}
                    >
                      Error creating document: {String(part.text.error)}
                    </div>
                  );
                }

                return (
                  <DocumentPreview
                    isReadonly={isReadonly}
                    key={toolCallId}
                    result={{
                      url: part.text,
                      kind: "text",
                      title: chatType
                    }}
                    isLast={isLast}
                  />

                );
              }

              return null;
            })}
          </div>
        </div>
        {displayTimestamp && (
          <div
            className={cn(
              "text-xs text-muted-foreground mt-1 px-2 weweq",
              message.role === "user" ? "text-right" : "text-left hidden"
            )}
          >
            {format(new Date(displayTimestamp), "MMM d, yyyy 'at' h:mm a")}
          </div>
        )}
      </motion.div>

      {showBuyCreditsModal && userData && (
        <BuyCreditsModal
          show={showBuyCreditsModal}
          handleClose={() => setShowBuyCreditsModal(false)}
          userDetail={userData}
        />
      )}

      {(generateLinkData || shareData) && shareOpen && userData && (
        <ShareLinkPopup
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          // isFreeLimit={isFreeLimit}
          generateData={generateLinkData || shareData}
          htmlContent={htmlContent || undefined}
          userDetail={userData}
        />
      )}
    </AnimatePresence>
  );
};

export const PreviewMessage = memo(PurePreviewMessage, (prevProps, nextProps) => {
  if (prevProps.isLoading !== nextProps.isLoading) return false;
  if (prevProps.message.id !== nextProps.message.id) return false;
  if (prevProps.requiresScrollPadding !== nextProps.requiresScrollPadding) return false;
  if (prevProps.isLast !== nextProps.isLast) return false;
  if (!equal(prevProps.message.parts, nextProps.message.parts)) return false;
  return true;
});

export const ThinkingMessage = () => {
  const role = "assistant";
  return (
    <motion.div
      data-testid="message-assistant-loading"
      className="w-full group/message mr-auto"
      initial={{ y: 5, opacity: 0 }}
      animate={{ y: 0, opacity: 1, transition: { delay: 1 } }}
      data-role={role}
    >
      <div
        className={cx(
          "flex gap-4 group-data-[role=user]/message:px-3 w-full group-data-[role=user]/message:w-fit group-data-[role=user]/message:ml-auto group-data-[role=user]/message:max-w-2xl group-data-[role=user]/message:py-2 rounded-xl",
          { "group-data-[role=user]/message:bg-muted": true }
        )}
      >
        <div className="size-8 flex items-center rounded-full justify-center ring-1 shrink-0 ring-border">
          <SparklesIcon size={14} />
        </div>
        <div className="flex flex-col gap-2 w-full">
          <div className="flex flex-col gap-4 text-muted-foreground">Hmm...</div>
        </div>
      </div>
    </motion.div>
  );
};
